import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const videoPlayerInputs = [
  {
    name: "videoType",
    friendlyName: "Hosting",
    type: "string",
    enum: ["youtube", "hosted internally"],
    defaultValue: "hosted internally",
  },

  // YouTube video configuration
  {
    name: "youtubeConfiguration",
    friendlyName: "YouTube Video configuration",
    type: "object",
    showIf: `options.get("videoType") === "youtube"`,
    subFields: [
      {
        name: "externalLinkVideo",
        friendlyName: "YouTube video link",
        type: "string",
        required: true,
      },
    ],
  },

  // Hosted video configuration
  {
    name: "hostedVideoConfiguration",
    friendlyName: "Hosted Video configuration",
    type: "object",
    required: true,
    defaultValue: {
      desktopSrc:
        "https://cdn.builder.io/o/assets%2F90293f7dc9704cb5935f357315020051%2Fdb83e0ca80d24c6da4c2fb863cd65ebb%2Fcompressed?apiKey=90293f7dc9704cb5935f357315020051&token=db83e0ca80d24c6da4c2fb863cd65ebb&alt=media&optimized=true",
    },
    showIf: `options.get("videoType") === "hosted internally"`,
    subFields: [
      {
        name: "loop",
        friendlyName: "Loop video",
        type: "boolean",
      },
      {
        name: "autoPlay",
        friendlyName: "Auto play video",
        type: "boolean",
      },
      {
        name: "desktopSrc",
        friendlyName: "Desktop video (>960px)",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        required: true,
      },
      {
        name: "tabletSrc",
        friendlyName: "Tablet video (480px to 959px)",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
      },
      {
        name: "mobileSrc",
        friendlyName: "Mobile video (<479px)",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
      },
    ],
  },

  // Common configuration for all video types
  {
    name: "controlColor",
    friendlyName: "Control color",
    type: "string",
    enum: ["dark", "light"],
    defaultValue: "dark",
  },
  {
    name: "posterImage",
    friendlyName: "Poster image",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "posterAlt",
    friendlyName: "Poster image alt",
    type: "string",
    defaultValue: "",
  },
  {
    name: "description",
    friendlyName: "Description",
    type: "string",
    defaultValue: "",
  },
  {
    name: "duration",
    friendlyName: "Duration",
    type: "boolean",
  },
  {
    name: "playLabel",
    friendlyName: "Play label",
    type: "string",
  },
  {
    name: "replayLabel",
    friendlyName: "Replay label",
    type: "string",
  },
  {
    name: "muteLabel",
    friendlyName: "Mute label",
    type: "string",
  },
  {
    name: "soundOnLabel",
    friendlyName: "Sound on label",
    type: "string",
  },
  {
    name: "showFullScreenLabel",
    friendlyName: "Show Fullscreen label",
    type: "string",
  },
  {
    name: "showFullscreen",
    friendlyName: "Enable fullscreen button",
    type: "boolean",
  },
  {
    name: "showControls",
    friendlyName: "Enable video controls",
    type: "boolean",
  },
  {
    name: "preventMainPlayButtonClick",
    friendlyName: "Prevent of main play button click",
    type: "boolean",
  },
  {
    name: "preload",
    friendlyName: "Preload the video",
    type: "boolean",
  },
  {
    name: "contentSize",
    friendlyName: "Player adjust to size of asset",
    type: "boolean",
  },
] as const satisfies Inputs;

export const VideoPlayerComponent: RegisteredComponent = {
  name: "VideoPlayer",
  component: dynamic(async () =>
    withValidation(
      videoPlayerInputs,
      (await import("./VideoPlayer")).VideoPlayer,
      "VideoPlayer"
    )
  ),
  inputs: videoPlayerInputs,
};
